import {
  MasonryEngineFormFieldNode,
  Validation,
} from '#types/masonry-engine-node.types.ts';
import { logWarning } from './utils/log';
import { masonryEngineDefaultValidationHandlerMap } from './masonry-engine-default-validation-handlers-map';
import type {
  MasonryEngineValidationHandlerMap,
  FormValidationResult,
  MasonryEngineValidationControllerType,
  ValidationHandler,
} from './types/masonry-engine-validation.types';

const validationHandlerMap = {
  ...(masonryEngineDefaultValidationHandlerMap as MasonryEngineValidationHandlerMap<Validation>),
};

/**
 * The `MasonryEngineValidationController` provides methods to handle form field validation
 * within the Masonry Engine. It includes functionality for registering validation handlers,
 * validating form fields, and updating field nodes with validation messages.
 *
 * @type {MasonryEngineValidationControllerType}
 *
 */
export const MasonryEngineValidationController: MasonryEngineValidationControllerType =
  {
    /**
     * Registers a validation handler for a specific validation name.
     *
     * @param validationType - The name of the validation to register.
     * @param handler - The validation handler function to register.
     *
     * @throws {Error} If a validation handler for the specified name already exists.
     *
     */
    registerValidationHandler: (validationType, handler) => {
      if (validationType in validationHandlerMap) {
        throw new Error(
          `Validation handler for ${validationType} already exists.`,
        );
      }
      validationHandlerMap[validationType] =
        handler as ValidationHandler<Validation>;
    },
    /**
     *
     * Validates an array of form field nodes against their defined validation rules.
     * @param fieldNodes - An array of form field nodes to validate.
     *
     * @returns {FormValidationResult} An object containing validation results for each field node.
     *
     */
    validateFormFields: (fieldNodes) => {
      const validationResult: FormValidationResult = [];

      fieldNodes.forEach((fieldData) => {
        const {
          nodeId,
          value,
          formFieldProperties: { formFieldId, validationRules },
        } = fieldData;

        // Perform validation checks.  Use optional chaining to prevent errors.
        if (!validationRules) {
          return;
        }

        // eslint-disable-next-line no-restricted-syntax
        for (const validationRule of validationRules) {
          const {
            validationType,
            payload: validationValue,
            statusMessage,
            statusType,
          } = validationRule;

          const validationHandler = validationHandlerMap[validationType];

          if (!validationHandler) {
            throw new Error(
              `No validation handler registered for ${validationType}.`,
            );
          }
          const isValid = validationHandler(value, validationValue);

          if (!isValid) {
            validationResult.push({
              nodeId,
              formFieldId,
              statusType,
              statusMessage,
            });
            break;
          }
        }
      });

      return validationResult;
    },
    /**
     * Updates the state of form field nodes with validation messages.
     *
     * @param validationResult - The validation results object containing status messages and types.
     * @param fieldNodes - An array of form field nodes to update.
     * @param stateController - The state controller to update the field nodes' states.
     *
     */
    updateFieldNodesWithValidationMessages: (
      validationResult,
      fieldNodes,
      stateController,
    ) => {
      fieldNodes.forEach((fieldData) => {
        const { nodeId } = fieldData;
        const fieldValidationResult = validationResult.find(
          (result) => result.nodeId === nodeId,
        );
        stateController.setNodeState<MasonryEngineFormFieldNode>(
          nodeId,
          (state) => ({
            ...state,
            formFieldProperties: {
              ...state.formFieldProperties,
              // set status type to undefined if there is no validation result
              // this will clear any previous validation status
              inputStatus: fieldValidationResult?.statusType || undefined,
              statusMessage: fieldValidationResult?.statusMessage || undefined,
            },
          }),
        );
      });
    },
    /**
     * Given a `ValidationResult` and the Messaging Controller provided to the action handler,
     * will focus the first invalid field in the form (or do nothing if there are no invalid fields).
     * @param validationResult - The validation results object containing status messages and types.
     * @param messagingController - the Messaging Controller provided to the action handler.
     */
    focusFirstInvalidField: (validationResult, messagingController) => {
      const rootElements = validationResult
        .map((result) => {
          const rootElement = messagingController.sendMessage(result.nodeId, {
            type: 'getRootElement',
            payload: {},
          });

          if (!rootElement) {
            logWarning(
              `The renderer for field node ${result.nodeId} did not provide a root element ref!`,
            );
          }

          return rootElement;
        })
        .filter((item) => !!item);

      /**
       * Determine which of the invalid field nodes is earliest in the DOM order.
       */
      const firstElementIndex = rootElements.reduce(
        (currentEarliestIndex, currentElement, currentIndex) => {
          if (!rootElements[currentEarliestIndex]) {
            logWarning(
              `The renderer for field node ${validationResult[currentIndex].nodeId} did not provide a root element ref!`,
            );

            return currentEarliestIndex;
          }
          if (
            // eslint-disable-next-line no-bitwise
            rootElements[currentEarliestIndex].compareDocumentPosition(
              currentElement,
            ) & Node.DOCUMENT_POSITION_FOLLOWING
          ) {
            return currentEarliestIndex;
          }

          return currentIndex;
        },
        0,
      );

      messagingController.sendMessage(
        validationResult[firstElementIndex].nodeId,
        {
          type: 'focus',
          payload: {},
        },
      );
    },
  };
